
import * as React from 'react';
import { switchLayout } from '../../../operations/switchLayout';
import { layoutTypeFacet, availableLayoutsFacet, defaultLayoutFacet } from '../../../facets/ScopeFacets';
import { connectToItemCache } from '../../decorators/connectToItemCache';
import { IItemCache } from '../../../models/IItemCacheObserver';
import { IActionProps, Action } from '../../action/Action';
import { connectToItemsScope } from '../../decorators/connectToItemsScope';
import { withCurrentItemSet, getItemKey } from '../../decorators/selectors';
import { getItemActionEngagement } from '../../../models/telemetry/getItemEngagement';
import { LayoutTypeFacet } from '../../../models/layouts/LayoutTypeFacet';
import { pack } from '../../../models/Facet';
import { errorFacet } from '../../../facets/DemandFacets';
import { switchLayoutActionControl } from '../../../controls/switchLayoutActionControl';

/**
 * @public
 */
export interface ISwitchLayoutActionProps {
  itemKey: string;
  itemsScopeItemKey: string;
  layoutType: LayoutTypeFacet;
}

/**
 * @public
 */
export const SwitchLayoutAction: React.ComponentType<Pick<ISwitchLayoutActionProps, 'layoutType'>> =
  withCurrentItemSet()(connectToItemsScope()(
    connectToItemCache((
      itemCache: IItemCache,
      props: ISwitchLayoutActionProps
    ): IActionProps => {
      const {
        itemKey,
        layoutType,
        itemsScopeItemKey
      } = props;

      const isError = itemCache.isItemFacetResolved(errorFacet, itemKey) && !!itemCache.demandItemFacet(errorFacet, itemKey);

      const availableLayouts = itemCache.demandItemFacet(availableLayoutsFacet, itemsScopeItemKey) || pack({}, {});

      const preferredLayoutType = itemCache.demandItemFacet(layoutTypeFacet, itemsScopeItemKey);
      const preferredLayout = preferredLayoutType && availableLayouts[preferredLayoutType.id] && preferredLayoutType || undefined;

      const defaultLayoutType = itemCache.demandItemFacet(defaultLayoutFacet, itemsScopeItemKey);
      const defaultLayout = defaultLayoutType && availableLayouts[defaultLayoutType.id] && defaultLayoutType || undefined;

      const isLayoutCurrent = (preferredLayout || defaultLayout) === layoutType;

      const { dispatch } = itemCache;
      const switchLayoutActionControlHandler = itemCache.demandItemFacet(switchLayoutActionControl, itemsScopeItemKey);
      const { onSwitchLayout } = switchLayoutActionControl.evaluate(switchLayoutActionControlHandler)(itemCache, {});

      return {
        isAvailable: !!itemKey && !isError && !isLayoutCurrent && !!availableLayouts && !!availableLayouts[layoutType.id],
        isToggled: isLayoutCurrent,
        ...getItemActionEngagement(itemCache, {
          itemKey: getItemKey(itemCache, { itemKey }),
          engagement: {
            name: 'SwitchLayout',
            isIntentional: true,
            extraData: {
              layoutType: layoutType.name
            }
          }
        }),
        onExecute: async () => {
          dispatch(switchLayout({
            itemsScopeItemKey: itemsScopeItemKey,
            layoutType: layoutType
          }));

          if (onSwitchLayout) {
            dispatch(onSwitchLayout({
              itemsScopeItemKey: itemsScopeItemKey,
              layoutType: layoutType
            }));
          }
        }
      };
    })(Action)
  ));
